import React, { useCallback, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { getIn } from 'formik'

import { choicesCTAByGoalSelector } from '../../../../modules/selectors/choices'

import MultipleOptionsField from '../../../Facebook/AdForms/AdFacebookEdit/MultipleOptionsField'
import Checkbox from '../../../../components/Form/Checkbox'
import Field from '../../../../components/Form/Field'
import FieldLabelWrapper from '../../../../features/components/Form/FieldLabelWrapper'
import ItemMediaFile from './ItemMediaFile'

import { formatOptionsList } from '../../../../features/formatters'
import { getCTAListLimitedToLeadGenerationObjective } from '../../../Facebook/AdForms/helpers'

import {
  CLICK_THROUGH_LINK,
  CALL_TO_ACTION,
  DESCRIPTION_BLANK,
  DESCRIPTION_ENTER,
  DESCRIPTION_OPTION,
  DESCRIPTIONS_LIST,
  HEADLINE_BLANK,
  HEADLINE_ENTER,
  HEADLINE_OPTION,
  HEADLINES_LIST,
  CUSTOMIZED_STORY_FILE,
  IS_CURRENT_MEDIA_CUSTOMIZED_WITH_STORY
} from '../../../Facebook/AdForms/fields'
import { FILE_URL } from '../../AdFileUpload/fields'
import {
  AdFacebookCreateContext,
  defaultAdFacebookCreateContext
} from '../../../Facebook/formsContexts/AdFacebookCreateFormContextProvider'

import { LEAD_GENERATION } from '../../../../constants/campaigns'

import useStyles from './styles'

const headlineMaxLength = 400
const descriptionMaxLength = 400

const ItemMediaFields = ({ formik, itemPath, isItemActive, allowMultiple, campaignObjective }) => {
  const { t } = useTranslation()

  const classes = useStyles()

  const ctaOptionsByGoal = useSelector(choicesCTAByGoalSelector(campaignObjective))

  const { preselectedValues } = useContext(AdFacebookCreateContext) || defaultAdFacebookCreateContext

  const { values, setFieldValue } = formik

  const fileUrl = getIn(values, `${itemPath}.${FILE_URL}`)
  const isCurrentMediaCustomizedWithStory = values[IS_CURRENT_MEDIA_CUSTOMIZED_WITH_STORY]

  const headlineEnter = getIn(values, `${itemPath}.${HEADLINE_OPTION}`) === HEADLINE_ENTER
  const descriptionEnter = getIn(values, `${itemPath}.${DESCRIPTION_OPTION}`) === DESCRIPTION_ENTER

  const formattedActionsList = useMemo(() => {
    const list = values[LEAD_GENERATION]
      ? getCTAListLimitedToLeadGenerationObjective(ctaOptionsByGoal)
      : ctaOptionsByGoal

    return formatOptionsList({
      list: list,
      labelName: 'label',
      valueName: 'name'
    })
  }, [ctaOptionsByGoal, values])

  const handleHeadlineCheckboxChange = useCallback(() => {
    setFieldValue(`${itemPath}.${HEADLINE_OPTION}`, headlineEnter ? HEADLINE_BLANK : HEADLINE_ENTER)
  }, [setFieldValue, itemPath, headlineEnter])

  const handleDescriptionCheckboxChange = useCallback(() => {
    setFieldValue(`${itemPath}.${DESCRIPTION_OPTION}`, descriptionEnter ? DESCRIPTION_BLANK : DESCRIPTION_ENTER)
  }, [setFieldValue, itemPath, descriptionEnter])

  return (
    <>
      {isCurrentMediaCustomizedWithStory ? (
        <>
          <FieldLabelWrapper label={t('Feed media file')}>
            <ItemMediaFile formik={formik} fileUrl={fileUrl} itemPath={itemPath} isItemActive={isItemActive} />
          </FieldLabelWrapper>
          <FieldLabelWrapper label={t('Stories and reels file')}>
            <ItemMediaFile
              formik={formik}
              fileUrl={fileUrl}
              itemPath={CUSTOMIZED_STORY_FILE}
              isItemActive={isItemActive}
            />
          </FieldLabelWrapper>
        </>
      ) : (
        <ItemMediaFile formik={formik} fileUrl={fileUrl} itemPath={itemPath} isItemActive={isItemActive} />
      )}

      <Checkbox
        id="headline"
        title="Add a headline"
        isLarge
        checked={headlineEnter}
        onCheck={handleHeadlineCheckboxChange}
        className={classes.checkbox}
      />
      {headlineEnter &&
        // todo fix rendering for allowMultiple, (currently it is not used in application)
        (allowMultiple ? (
          <FieldLabelWrapper label={t('Headline(s)')} labelFor={`${itemPath}.${HEADLINES_LIST}[0].text`}>
            <MultipleOptionsField
              fieldLabel="Headline"
              fieldMaxLength={headlineMaxLength}
              listFieldName={HEADLINES_LIST}
              formik={formik}
              allowMultiple={allowMultiple}
              itemPath={itemPath}
            />
          </FieldLabelWrapper>
        ) : (
          <Field
            placeholder="HEADLINE"
            formik={formik}
            name={`${itemPath}.${HEADLINES_LIST}[0].text`}
            isTextarea
            maxLength={headlineMaxLength}
            enableReinitialize
            className={classes.field}
          />
        ))}
      <Checkbox
        id="description"
        title="Add a description"
        isLarge
        checked={descriptionEnter}
        onCheck={handleDescriptionCheckboxChange}
        className={classes.checkbox}
      />
      {descriptionEnter &&
        (allowMultiple ? (
          <FieldLabelWrapper label={t('Description(s)')} labelFor={`${itemPath}.${DESCRIPTIONS_LIST}[0].text`}>
            <MultipleOptionsField
              fieldLabel="Description"
              formik={formik}
              listFieldName={DESCRIPTIONS_LIST}
              allowMultiple={allowMultiple}
            />
          </FieldLabelWrapper>
        ) : (
          <Field
            placeholder="DESCRIPTION"
            formik={formik}
            name={`${itemPath}.${DESCRIPTIONS_LIST}[0].text`}
            maxLength={descriptionMaxLength}
            isTextarea
            enableReinitialize
            className={classes.field}
          />
        ))}
      {!preselectedValues?.[CALL_TO_ACTION] && (
        <FieldLabelWrapper label={t('Call to action')} labelFor={`${itemPath}.${CALL_TO_ACTION}`}>
          <Field
            placeholder="Call to action"
            formik={formik}
            name={`${itemPath}.${CALL_TO_ACTION}`}
            options={formattedActionsList}
            className={classes.field}
          />
        </FieldLabelWrapper>
      )}
      {!preselectedValues?.[CLICK_THROUGH_LINK] && (
        <FieldLabelWrapper label={t('Click through link')} labelFor={`${itemPath}.${CLICK_THROUGH_LINK}`}>
          <Field
            formik={formik}
            name={`${itemPath}.${CLICK_THROUGH_LINK}`}
            placeholder="Click through link"
            autoComplete="off"
          />
        </FieldLabelWrapper>
      )}
    </>
  )
}

ItemMediaFields.propTypes = {
  formik: PropTypes.object,
  itemPath: PropTypes.string,
  isItemActive: PropTypes.bool,
  allowMultiple: PropTypes.bool,
  isStepFieldsValid: PropTypes.func
}

export default ItemMediaFields
